import React, { useState, useEffect } from 'react';
import {
    Box, Button, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Dialog, DialogTitle, DialogContent,
    DialogActions, IconButton, Typography, CircularProgress, Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import { getAllKeyValuesV2, setKeyValueV2 } from '../../api/admin';
import logger from '../../utils/logger';
import './AdminView.css';

const KeyValueTab = ({ auth }) => {
    const [keyValues, setKeyValues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editDialog, setEditDialog] = useState(false);
    const [currentEdit, setCurrentEdit] = useState({ key: '', description: '', value: '' });
    const [newKeyDialog, setNewKeyDialog] = useState(false);
    const [viewValueDialog, setViewValueDialog] = useState(false);
    const [currentViewValue, setCurrentViewValue] = useState({ key: '', value: '' });

    const loadKeyValues = async () => {
        setLoading(true);
        try {
            await new Promise((resolve) => {
                getAllKeyValuesV2(auth, resolve);
            }).then((response) => {
                if (Array.isArray(response)) {
                    setKeyValues(response);
                }
            });
        } catch (error) {
            logger.error('Error loading key-values:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadKeyValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth]);

    const handleSave = async (isNew = false) => {
        setLoading(true);
        try {
            await new Promise((resolve) => {
                setKeyValueV2(auth, resolve, currentEdit);
            });
            await loadKeyValues();
            if (isNew) {
                setNewKeyDialog(false);
            } else {
                setEditDialog(false);
            }
        } catch (error) {
            logger.error('Error saving key-value:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (keyValue) => {
        setCurrentEdit(keyValue);
        setEditDialog(true);
    };

    const handleNewKey = () => {
        setCurrentEdit({ key: '', description: '', value: '' });
        setNewKeyDialog(true);
    };

    const handleViewValue = (keyValue) => {
        setCurrentViewValue(keyValue);
        setViewValueDialog(true);
    };

    const truncateText = (text, maxLength = 50) => {
        if (text && text.length > maxLength) {
            return text.substring(0, maxLength) + '...';
        }
        return text;
    };

    if (loading && keyValues.length === 0) {
        return (
            <Box className="admin-loading">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="admin-tab-content">
            {/* Header */}
            <Box className="admin-section-header">
                <Typography variant="h6">Key-Value Store</Typography>
                <Box className="admin-actions">
                    <Button
                        variant="outlined"
                        onClick={loadKeyValues}
                        startIcon={<RefreshIcon />}
                        size="small"
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleNewKey}
                        startIcon={<AddIcon />}
                        size="small"
                    >
                        Add Key
                    </Button>
                </Box>
            </Box>

            {/* Desktop Table */}
            <Box className="admin-table-desktop">
                <TableContainer component={Paper} className="admin-table-container">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Key</TableCell>
                                <TableCell className="admin-hide-mobile">Description</TableCell>
                                <TableCell>Value</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {keyValues.map((kv) => (
                                <TableRow key={kv.key} className="admin-table-row">
                                    <TableCell className="admin-key-cell">
                                        <code>{kv.key}</code>
                                    </TableCell>
                                    <TableCell className="admin-hide-mobile admin-desc-cell">
                                        {truncateText(kv.description, 60)}
                                    </TableCell>
                                    <TableCell className="admin-value-cell">
                                        <Box className="admin-value-preview">
                                            <span>{truncateText(kv.value, 40)}</span>
                                            {kv.value && kv.value.length > 40 && (
                                                <Tooltip title="View full value">
                                                    <IconButton onClick={() => handleViewValue(kv)} size="small">
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleEdit(kv)} size="small">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {keyValues.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        No key-value pairs found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Mobile Cards */}
            <Box className="admin-cards-mobile">
                {keyValues.length === 0 && (
                    <Paper className="admin-empty-state">
                        <Typography>No key-value pairs found</Typography>
                    </Paper>
                )}
                {keyValues.map((kv) => (
                    <Paper key={kv.key} className="admin-kv-card">
                        <Box className="admin-kv-card-header">
                            <code className="admin-kv-key">{kv.key}</code>
                        </Box>
                        {kv.description && (
                            <Typography className="admin-kv-desc">
                                {truncateText(kv.description, 80)}
                            </Typography>
                        )}
                        <Box className="admin-kv-value">
                            <Typography variant="caption" color="textSecondary">Value:</Typography>
                            <Typography className="admin-kv-value-text">
                                {truncateText(kv.value, 100)}
                            </Typography>
                        </Box>
                        <Box className="admin-kv-card-actions">
                            {kv.value && kv.value.length > 100 && (
                                <Button
                                    size="small"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => handleViewValue(kv)}
                                >
                                    View
                                </Button>
                            )}
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => handleEdit(kv)}
                            >
                                Edit
                            </Button>
                        </Box>
                    </Paper>
                ))}
            </Box>

            {/* Edit Dialog */}
            <Dialog
                open={editDialog}
                onClose={() => setEditDialog(false)}
                maxWidth="md"
                fullWidth
                className="admin-dialog"
            >
                <DialogTitle>Edit Key-Value</DialogTitle>
                <DialogContent>
                    <Box className="admin-dialog-form">
                        <TextField
                            label="Key"
                            value={currentEdit.key}
                            disabled
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Description"
                            value={currentEdit.description}
                            onChange={(e) => setCurrentEdit({ ...currentEdit, description: e.target.value })}
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                        />
                        <TextField
                            label="Value"
                            value={currentEdit.value}
                            onChange={(e) => setCurrentEdit({ ...currentEdit, value: e.target.value })}
                            fullWidth
                            multiline
                            rows={8}
                            size="small"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog(false)}>Cancel</Button>
                    <Button onClick={() => handleSave(false)} variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* New Key Dialog */}
            <Dialog
                open={newKeyDialog}
                onClose={() => setNewKeyDialog(false)}
                maxWidth="md"
                fullWidth
                className="admin-dialog"
            >
                <DialogTitle>Add New Key-Value</DialogTitle>
                <DialogContent>
                    <Box className="admin-dialog-form">
                        <TextField
                            label="Key"
                            value={currentEdit.key}
                            onChange={(e) => setCurrentEdit({ ...currentEdit, key: e.target.value })}
                            fullWidth
                            size="small"
                            placeholder="e.g., config:my_setting"
                        />
                        <TextField
                            label="Description"
                            value={currentEdit.description}
                            onChange={(e) => setCurrentEdit({ ...currentEdit, description: e.target.value })}
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                        />
                        <TextField
                            label="Value"
                            value={currentEdit.value}
                            onChange={(e) => setCurrentEdit({ ...currentEdit, value: e.target.value })}
                            fullWidth
                            multiline
                            rows={8}
                            size="small"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewKeyDialog(false)}>Cancel</Button>
                    <Button onClick={() => handleSave(true)} variant="contained">
                        Add
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Value Dialog */}
            <Dialog
                open={viewValueDialog}
                onClose={() => setViewValueDialog(false)}
                maxWidth="md"
                fullWidth
                className="admin-dialog"
            >
                <DialogTitle>
                    <code>{currentViewValue.key}</code>
                </DialogTitle>
                <DialogContent>
                    <Paper className="admin-code-block">
                        <pre>{currentViewValue.value}</pre>
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewValueDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default KeyValueTab;